import * as THREE from 'three';
import { strFromU8, unzipSync } from 'fflate';
import type {
  FabricInfo,
  FabricPath,
  FabricResult,
  FabricSide,
  ImageFabricPath,
  SfabFabricPath,
  SideConfig,
  TextureInfo
} from './typing';

export class FabricLoader extends THREE.Loader {
  private textureLoader = new THREE.TextureLoader();

  constructor(manager: THREE.LoadingManager = THREE.DefaultLoadingManager) {
    super(manager);
  }

  override load(
    url: string,
    onLoad?: (result: FabricResult) => void,
    onProgress?: (event: ProgressEvent<EventTarget>) => void,
    onError?: (err: Error) => void
  ): void {
    try {
      const fabricPath: FabricPath = JSON.parse(url) as FabricPath;
      this.loadFabric(fabricPath, onLoad, onProgress, onError);
    } catch (e) {
      if (onError) onError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  async loadFabric(
    fabric: FabricPath,
    onLoad?: (result: FabricResult) => void,
    onProgress?: (event: ProgressEvent<EventTarget>) => void,
    onError?: (err: Error) => void
  ): Promise<void> {
    try {
      let result: FabricResult;

      if ('model3dPath' in fabric && fabric.model3dPath) {
        result = await this.loadSfabFabric(fabric as SfabFabricPath, onProgress);
      } else if ('xhrPath' in fabric && fabric.xhrPath) {
        result = await this.loadImageFabric(fabric as ImageFabricPath, onProgress);
      } else {
        throw new Error('Fabric path is not properly defined');
      }

      if (onLoad) onLoad(result);
    } catch (error) {
      console.error('Error loading fabric:', error);
      if (onError) onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private async loadSfabFabric(
    sfab: SfabFabricPath,
    onProgress?: (event: ProgressEvent<EventTarget>) => void
  ): Promise<FabricResult> {
    try {
      const response = await fetch(sfab.model3dPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch fabric file: ${response.statusText}`);
      }
      const massiveFileBuf = await response.arrayBuffer();
      const massiveFile = new Uint8Array(massiveFileBuf);
      const unzipped = unzipSync(massiveFile);

      if (!unzipped['fabric.json']) {
        throw new Error('fabric.json not found in the archive');
      }

      const fabricInfo: FabricInfo = JSON.parse(strFromU8(unzipped['fabric.json']));
      const result: FabricResult = {
        type: 'sfab',
        front: {} as FabricSide
      };

      // Process front side textures
      await this.processFabricSide(
        result.front,
        fabricInfo.material.renderConfig.front,
        unzipped
      );

      // Process back side if it exists
      if (fabricInfo.material.renderConfig.back) {
        const backConfig = fabricInfo.material.renderConfig.back;

        if (!backConfig.sameWithFront) {
          result.back = {} as FabricSide;
          await this.processFabricSide(
            result.back,
            backConfig,
            unzipped
          );
        }
      }
      return result;
    } catch (error) {
      throw new Error(`Error in loadSfabFabric: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private async loadImageFabric(
    image: ImageFabricPath,
    onProgress?: (event: ProgressEvent<EventTarget>) => void
  ): Promise<FabricResult> {
    try {
      const texture = await this.createTexture(image.xhrPath);
      return {
        type: 'image',
        front: {
          baseColor: {
            path: image.xhrPath,
            texture,
            width: image.width,
            height: image.height
          }
        }
      };
    } catch (error) {
      throw new Error(`Error in loadImageFabric: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private async processFabricSide(
    targetSide: FabricSide,
    config: SideConfig,
    unzipped: Record<string, Uint8Array>
  ): Promise<void> {
    const fabricSideKeys: Array<keyof FabricSide> = [
      'baseColor', 'alpha', 'displacement', 'normal'
    ];

    const processingPromises: Promise<void>[] = [];
    for (const type of fabricSideKeys) {
      if (config[type]) {
        const texturePath = config[type]?.texture?.path;
        if (texturePath && unzipped[texturePath]) {
          const promise = this.loadSfabTextureItem(
            { ...config[type]?.texture }, unzipped[texturePath])
            .then(textureInfo => {
              targetSide[type] = textureInfo;
            });

          processingPromises.push(promise);
        }
      }
    }

    await Promise.all(processingPromises);
  }

  private async loadSfabTextureItem(
    t: Omit<TextureInfo, 'texture'>,
    data: Uint8Array
  ): Promise<TextureInfo> {
    const blob = new Blob([data]);
    const blobUrl = URL.createObjectURL(blob);

    try {
      const texture = await this.createTexture(blobUrl);
      return {
        ...t,
        texture
      };
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }

  private createTexture(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          resolve(texture);
        },
        undefined,
        (error: any) => reject(new Error(`Failed to load texture: ${error?.message}`))
      );
    });
  }
}